(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;
  const cleaners = [];
  const on = (target, event, handler, options) => {
    target?.addEventListener(event, handler, options);
    cleaners.push(() => target?.removeEventListener(event, handler, options));
  };

  const normalizePath = path => (path.replace(/\/+$/, '') || '/');

  function targetFromLink(link) {
    if (!link?.href) return null;
    let url;
    try { url = new URL(link.href, location.href); } catch (_) { return null; }
    if (url.origin !== location.origin || normalizePath(url.pathname) !== normalizePath(location.pathname) || !url.hash) return null;
    const id = decodeURIComponent(url.hash.slice(1));
    if (!id) return null;
    const target = document.getElementById(id);
    if (!target || !target.id) return null;
    return { target, url };
  }

  function scrollToTarget(target, url) {
    const header = document.querySelector('.a7-header');
    const offset = (header?.getBoundingClientRect().height || 0) + 14;
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
    window.scrollTo({
      top,
      behavior: reduced.matches ? 'auto' : 'smooth'
    });
    if (url?.hash && location.hash !== url.hash) {
      try { history.pushState(null, '', url.hash); } catch (_) { }
    }
  }

  function mountSmoothAnchors(scope = document) {
    const click = event => {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download') || link.target === '_blank' || link.dataset.noSmoothScroll != null) return;
      const found = targetFromLink(link);
      if (!found) return;
      event.preventDefault();
      document.querySelectorAll('dialog[open]').forEach(dialog => {
        try { dialog.close(); } catch (_) {}
      });
      requestAnimationFrame(() => scrollToTarget(found.target, found.url));
    };
    on(scope, 'click', click);
  }

  function markSideTargets(scope = document) {
    const splitVisuals = [...scope.querySelectorAll('.a7-split .a7-split-visual')];
    splitVisuals.forEach(node => {
      const section = node.closest('.a7-split');
      node.dataset.sideReveal = section?.classList.contains('a7-reverse') ? 'right' : 'left';
      node.dataset.reveal = 'none';
    });
    scope.querySelectorAll('.a7-purchase .a7-media').forEach(node => {
      node.dataset.sideReveal = 'right';
      node.dataset.reveal = 'none';
    });
    scope.querySelectorAll('.a7-contained-story > .a7-media').forEach(node => {
      node.dataset.sideReveal = 'right';
    });
  }

  function mountSideReveals(scope = document) {
    markSideTargets(scope);
    const targets = [...scope.querySelectorAll('[data-side-reveal]')];
    if (!targets.length) return () => {};
    if (reduced.matches || root.classList.contains('shopify-design-mode')) {
      targets.forEach(node => node.classList.add('is-side-in'));
      return () => {};
    }
    root.classList.add('a7-motion-ready');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-side-in');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    targets.forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }

  function mountParallax(scope = document) {
    const sections = [...scope.querySelectorAll('[data-a7-parallax]')];
    if (!sections.length) return () => {};
    let frame = 0;
    let active = true;
    const render = () => {
      frame = 0;
      if (!active || document.hidden) return;
      sections.forEach(section => {
        const media = section.querySelector('.a7-parallax__media');
        if (!media) return;
        if (reduced.matches || root.classList.contains('shopify-design-mode')) {
          media.style.transform = 'translate3d(0,0,0) scale(1.06)';
          return;
        }
        const rect = section.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > innerHeight) return;
        const strength = Math.max(0, Math.min(90, Number(section.dataset.parallaxStrength || 42)));
        const travel = (innerHeight - rect.top) / (innerHeight + rect.height) - 0.5;
        const shift = travel * strength * 2;
        media.style.transform = `translate3d(0,${shift.toFixed(2)}px,0) scale(1.08)`;
      });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(render); };
    on(window, 'scroll', schedule, { passive: true });
    on(window, 'resize', schedule, { passive: true });
    on(document, 'visibilitychange', schedule);
    on(reduced, 'change', schedule);
    schedule();
    return () => {
      active = false;
      if (frame) cancelAnimationFrame(frame);
    };
  }

  function mount(scope = document) {
    markSideTargets(scope);
    const cleanSide = mountSideReveals(scope);
    const cleanParallax = mountParallax(scope);
    return () => { cleanSide(); cleanParallax(); };
  }

  function boot() {
    mountSmoothAnchors(document);
    mount(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', event => mount(event.target));
  window.A7Motion = { mount, targetFromLink, scrollToTarget };
})();